import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { publicApiClient } from '../apiClient';
import { useSystemStatus } from '../contexts/SystemStatusContext';
import MaintenancePage from './Maintenance';
import { ExternalLink } from 'lucide-react';
import SEO from '../components/common/SEO';
import Breadcrumbs from '../components/common/Breadcrumbs';

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { isMaintenanceActive } = useSystemStatus();

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await publicApiClient.post('/v1/send-registration-email', { email });
      const data = await response.json();

      if (response.ok) {
        setMessage(data.message || 'ご指定のメールアドレスに確認メールを送信しました。メールをご確認の上、パスワード設定へお進みください。');
      } else {
        setError(data.error || '登録に失敗しました。時間をおいて再度お試しください。');
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError('ネットワークエラーが発生しました。接続を確認して再度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  if (isMaintenanceActive) {
      return <MaintenancePage />;
  }

  return (
    <>
      <SEO 
        title="無料で始める新規会員登録｜Stripe請求書発行 Billwave"
        description="Stripe請求書CSV一括発行サービス「Billwave」の無料新規登録ページです。毎月5件まで無料で請求書一括作成・予約発行機能をお試しいただけます。経理業務の効率化を今すぐ始めましょう。"
        path="/signup"
      />
      <div className="w-full max-w-lg mx-auto py-4 px-2">
        <Breadcrumbs items={[{ label: '新規会員登録' }]} />

        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md border border-gray-100">
          <h1 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
            無料アカウント作成
          </h1>
          
          <p className="text-center text-gray-600 mb-6 text-sm sm:text-base leading-relaxed">
            ご登録のメールアドレスを入力してください。<br/>
            パスワード設定用の確認リンクをお送りします。
          </p>

          <form onSubmit={handleSendEmail} className="flex flex-col gap-5">
            <div>
              <label htmlFor="email" className="block mb-1 font-bold text-gray-700 text-sm">メールアドレス</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="例: user@example.com"
                required
                className="w-full p-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-4 text-lg font-bold text-white rounded-md shadow transition-colors ${
                loading ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? '送信中...' : 'メールを送信して無料で始める'}
            </button>

            {message && <p className="text-green-700 font-medium text-center bg-green-50 p-3 rounded-md text-sm">{message}</p>}
            {error && (
              <p className="text-red-600 text-center bg-red-50 p-3 rounded-md text-sm">
                {error} {error.includes('既に使用されています') && <Link to="/login" className="text-blue-600 font-bold underline ml-1">ログイン画面へ</Link>}
              </p>
            )}
          </form>
          
          <div className="text-center mt-6 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              既にアカウントをお持ちですか？ <Link to="/login" className="text-blue-600 font-bold hover:underline">ログイン画面へ</Link>
            </p>
            <div className="mt-2">
              <Link to="/support/how-to-use" className="text-xs text-blue-600 hover:underline">
                使い方ガイドを見る
              </Link>
            </div>
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg text-center text-xs text-gray-600 leading-relaxed">
            <p className="mb-2">
              登録を進めることで、以下の各種利用規約・各種方針に同意したものとみなします。
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/terms" target="_blank" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                利用規約 <ExternalLink size={12} />
              </Link>
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                個人情報保護方針 <ExternalLink size={12} />
              </Link>
              <Link to="/tokushoho" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                特定商取引法に基づく表記 <ExternalLink size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;
